import React, { useState, useEffect } from 'react';
import { 
  Card, 
  CardHeader, 
  CardContent, 
  Button, 
  Typography, 
  Box, 
  CircularProgress, 
  Grid, 
  Tabs, 
  Tab, 
  Table, 
  TableBody, 
  TableCell, 
  TableContainer, 
  TableHead, 
  TableRow, 
  TablePagination,
  Paper,
  Stack,
  IconButton
} from '@mui/material';
import { 
  Edit as EditIcon, 
  Delete as DeleteIcon, 
  Add as AddIcon, 
  ArrowBack as ArrowBackIcon 
} from '@mui/icons-material';
import { productHierarchyService } from '../../services/productHierarchy.service';

function TabPanel(props) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`simple-tabpanel-${index}`}
      aria-labelledby={`simple-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ p: 3 }}>
          {children}
        </Box>
      )}
    </div>
  );
}

const HierarchyNodeDetail = ({ nodeId, onBack, onEdit, onNodeSelect }) => {
  const [nodeData, setNodeData] = useState(null);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [productLoading, setProductLoading] = useState(false);
  const [tabValue, setTabValue] = useState(0);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalProducts, setTotalProducts] = useState(0);
  
  useEffect(() => {
    loadNodeData();
  }, [nodeId]);
  
  const loadNodeData = async () => {
    setLoading(true);
    try {
      const response = await productHierarchyService.getNodeById(nodeId);
      setNodeData(response.data);
      loadProducts(0, rowsPerPage);
    } catch (error) {
      console.error('Failed to load node details', error);
    } finally {
      setLoading(false);
    }
  };
  
  const loadProducts = async (newPage, pageSize) => {
    setProductLoading(true);
    try {
      const response = await productHierarchyService.getNodeProducts(nodeId, pageSize, newPage * pageSize);
      setProducts(response.data.products);
      setTotalProducts(response.data.total_products);
    } catch (error) {
      console.error('Failed to load products', error);
    } finally {
      setProductLoading(false);
    }
  };
  
  const handleChangePage = (event, newPage) => {
    setPage(newPage);
    loadProducts(newPage, rowsPerPage);
  };

  const handleChangeRowsPerPage = (event) => {
    const newRowsPerPage = parseInt(event.target.value, 10);
    setRowsPerPage(newRowsPerPage);
    setPage(0);
    loadProducts(0, newRowsPerPage);
  };
  
  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };
  
  const handleDeleteNode = async () => {
    try {
      await productHierarchyService.deleteNode(nodeId);
      console.log('Node deleted successfully');
      onBack();
    } catch (error) {
      console.error('Failed to delete node', error);
    }
  };
  
  if (loading) {
    return (
      <Card>
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
          <CircularProgress />
        </Box>
      </Card>
    );
  }
  
  if (!nodeData) {
    return (
      <Card>
        <CardContent>
          <Typography>No node details available</Typography>
        </CardContent>
      </Card>
    );
  }
  
  return (
    <Card>
      <CardHeader 
        title={nodeData ? nodeData.name : 'Node Details'} 
        subheader={nodeData ? `Level: ${nodeData.level}` : ''}
        action={
          <Stack direction="row" spacing={1}>
            <Button 
              startIcon={<ArrowBackIcon />} 
              onClick={onBack}
            >
              Back
            </Button>
            {/* Hide the Edit button for now
            {nodeData && onEdit && (
              <Button 
                variant="outlined" 
                startIcon={<EditIcon />} 
                onClick={() => onEdit(nodeData)}
                disabled={true} // Disable the button
              >
                Edit
              </Button>
            )}
            */}
          </Stack>
        }
      />
      <CardContent>
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={3}>
            <Paper sx={{ p: 2, textAlign: 'center' }}>
              <Typography variant="h6">{nodeData.sku_count || 0}</Typography>
              <Typography variant="body2" color="text.secondary">SKU Count</Typography>
            </Paper>
          </Grid>
          <Grid item xs={3}>
            <Paper sx={{ p: 2, textAlign: 'center' }}>
              <Typography variant="h6">{nodeData.active_sku_count || 0}</Typography>
              <Typography variant="body2" color="text.secondary">Active SKUs</Typography>
            </Paper>
          </Grid>
          <Grid item xs={3}>
            <Paper sx={{ p: 2, textAlign: 'center' }}>
              <Typography variant="h6">
                {nodeData.avg_unit_cost ? `$${parseFloat(nodeData.avg_unit_cost).toFixed(2)}` : 'N/A'}
              </Typography>
              <Typography variant="body2" color="text.secondary">Avg. Unit Cost</Typography>
            </Paper>
          </Grid>
          <Grid item xs={3}>
            <Paper sx={{ p: 2, textAlign: 'center' }}>
              <Typography variant="h6">{nodeData.parent_name || 'None'}</Typography>
              <Typography variant="body2" color="text.secondary">Parent</Typography>
            </Paper>
          </Grid>
        </Grid>
        
        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tabs value={tabValue} onChange={handleTabChange}>
            <Tab label="Products" />
            <Tab label="Children" />
          </Tabs>
        </Box>
        
        <TabPanel value={tabValue} index={0}>
          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>SKU</TableCell>
                  <TableCell>Product Name</TableCell>
                  <TableCell>Unit Cost</TableCell>
                  <TableCell>Status</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {productLoading ? (
                  <TableRow>
                    <TableCell colSpan={4} align="center">
                      <CircularProgress size={24} />
                    </TableCell>
                  </TableRow>
                ) : products.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={4} align="center">No products found</TableCell>
                  </TableRow>
                ) : (
                  products.map((product) => (
                    <TableRow key={product.product_id}>
                      <TableCell>{product.sku}</TableCell>
                      <TableCell>{product.description}</TableCell>
                      <TableCell>${parseFloat(product.unit_cost).toFixed(2)}</TableCell>
                      <TableCell>{product.product_life_cycle_status}</TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
            <TablePagination
              rowsPerPageOptions={[5, 10, 25]}
              component="div"
              count={totalProducts}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
            />
          </TableContainer>
        </TabPanel>
        
        <TabPanel value={tabValue} index={1}>
          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Name</TableCell>
                  <TableCell>Level</TableCell>
                  <TableCell>SKU Count</TableCell>
                  <TableCell>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {nodeData.children && nodeData.children.length > 0 ? (
                  nodeData.children.map((child) => (
                    <TableRow key={child.node_id}>
                      <TableCell>{child.name}</TableCell>
                      <TableCell>{child.level}</TableCell>
                      <TableCell>{child.sku_count}</TableCell>
                      <TableCell>
                        <Stack direction="row" spacing={1}>
                          <Button 
                            size="small" 
                            variant="outlined"
                            onClick={() => onNodeSelect(child.node_id)}
                          >
                            View
                          </Button>
                          <IconButton size="small">
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </Stack>
                      </TableCell>
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={4} align="center">No children found</TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </TabPanel>
      </CardContent>
    </Card>
  );
};

export default HierarchyNodeDetail; 